import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const ParentHelp = () => {
  const routes = all_routes;
  const [activeCategory, setActiveCategory] = useState("general");
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const faqData: FAQItem[] = [
    {
      id: "1",
      category: "general",
      question: "How do I check my child's attendance?",
      answer:
        "Navigate to the Dashboard and click on the Attendance Overview widget, or go to Menu > Attendance History for detailed records.",
    },
    {
      id: "2",
      category: "general",
      question: "How can I view homework assignments?",
      answer:
        "Click on the Homework tab in the bottom navigation or access it from the Dashboard's Homework Overview widget.",
    },
    {
      id: "3",
      category: "communication",
      question: "How do I contact my child's teacher?",
      answer:
        "Use the FAB button (+ icon) and select 'Send Message to Teacher', or go to Menu > Messages to start a conversation.",
    },
    {
      id: "4",
      category: "communication",
      question: "How can I schedule a parent-teacher meeting?",
      answer:
        "Click the FAB button and select 'Request Meeting', or send a message to the teacher requesting a meeting time.",
    },
    {
      id: "5",
      category: "payments",
      question: "Where can I view payment history?",
      answer:
        "Go to Menu > Payments History to see all past payments and upcoming dues.",
    },
    {
      id: "6",
      category: "payments",
      question: "How do I make a payment?",
      answer:
        "Click 'Quick Pay' from the Payments Overview widget on the Dashboard or go to Menu > Payments History.",
    },
    {
      id: "7",
      category: "documents",
      question: "How do I request official documents?",
      answer:
        "Use the FAB button and select 'Request Document', or go to Menu > Documents & Document Request Status.",
    },
    {
      id: "8",
      category: "documents",
      question: "Where can I download my child's report card?",
      answer:
        "Check the Documents Overview widget on the Dashboard or go to Menu > Documents for all available documents.",
    },
    {
      id: "9",
      category: "technical",
      question: "I'm not receiving notifications. What should I do?",
      answer:
        "Go to Menu > Settings > Notifications and check your notification preferences. Ensure your email and phone number are correct.",
    },
    {
      id: "10",
      category: "technical",
      question: "How do I change my password?",
      answer:
        "Go to Menu > Settings > Security and use the 'Change Password' section to update your credentials.",
    },
  ];

  const categories = [
    { id: "general", name: "General", icon: "ti-help" },
    { id: "communication", name: "Communication", icon: "ti-message" },
    { id: "payments", name: "Payments", icon: "ti-credit-card" },
    { id: "documents", name: "Documents", icon: "ti-file-text" },
    { id: "technical", name: "Technical", icon: "ti-settings" },
  ];

  const filteredFAQs = faqData.filter((faq) => {
    const matchesCategory =
      activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch =
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFAQ = (id: string) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content" style={{ paddingBottom: "80px" }}>
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Help & Support</h3>
              <p className="text-muted mb-0">
                Find answers to common questions
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="ti ti-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="d-flex align-items-center p-3 bg-primary-light rounded">
                    <div className="avatar avatar-sm bg-primary me-3">
                      <i className="ti ti-message text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-0">Contact Support</h6>
                      <small className="text-muted">Get direct help</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex align-items-center p-3 bg-info-light rounded">
                    <div className="avatar avatar-sm bg-info me-3">
                      <i className="ti ti-video text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-0">Video Tutorials</h6>
                      <small className="text-muted">Learn visually</small>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex align-items-center p-3 bg-success-light rounded">
                    <div className="avatar avatar-sm bg-success me-3">
                      <i className="ti ti-phone text-white"></i>
                    </div>
                    <div>
                      <h6 className="mb-0">Call Support</h6>
                      <small className="text-muted">+1 (555) 123-4567</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Browse by Category</h5>
            </div>
            <div className="card-body">
              <div className="btn-group flex-wrap" role="group">
                <button
                  className={`btn ${
                    activeCategory === "all"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  } mb-2`}
                  onClick={() => setActiveCategory("all")}
                >
                  <i className="ti ti-list me-2"></i>All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={`btn ${
                      activeCategory === category.id
                        ? "btn-primary"
                        : "btn-outline-primary"
                    } mb-2`}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <i className={`${category.icon} me-2`}></i>
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                Frequently Asked Questions
                {searchQuery && ` (${filteredFAQs.length} results)`}
              </h5>
            </div>
            <div className="card-body p-0">
              {filteredFAQs.length > 0 ? (
                <div className="accordion">
                  {filteredFAQs.map((faq, index) => (
                    <div
                      key={faq.id}
                      className="accordion-item border-0 border-bottom"
                    >
                      <div className="accordion-header">
                        <button
                          className={`accordion-button ${
                            expandedFAQ === faq.id ? "" : "collapsed"
                          }`}
                          onClick={() => toggleFAQ(faq.id)}
                          style={{ background: "none", border: "none" }}
                        >
                          <div className="d-flex align-items-center w-100">
                            <span className="badge bg-light text-muted me-3">
                              {
                                categories.find((c) => c.id === faq.category)
                                  ?.name
                              }
                            </span>
                            <span className="flex-fill text-start">
                              {faq.question}
                            </span>
                          </div>
                        </button>
                      </div>
                      {expandedFAQ === faq.id && (
                        <div className="accordion-body bg-light">
                          <p className="mb-0">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="avatar avatar-lg bg-light mb-3">
                    <i className="ti ti-search text-muted fs-2"></i>
                  </div>
                  <h5>No results found</h5>
                  <p className="text-muted">
                    Try adjusting your search or browse different categories.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="card mt-4">
            <div className="card-header">
              <h5 className="card-title mb-0">Still Need Help?</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6>Contact Information</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="ti ti-phone me-2 text-primary"></i>
                      <strong>Phone:</strong> +1 (555) 123-4567
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-mail me-2 text-primary"></i>
                      <strong>Email:</strong> support@school.edu
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-clock me-2 text-primary"></i>
                      <strong>Hours:</strong> Mon-Fri 8:00 AM - 6:00 PM
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Office Address</h6>
                  <address>
                    <strong>School Administration Office</strong>
                    <br />
                    123 Education Street
                    <br />
                    Learning City, State 12345
                    <br />
                    <i className="ti ti-map-pin me-2 text-primary"></i>
                    <a href="#" className="text-decoration-none">
                      View on Map
                    </a>
                  </address>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div
        className="bottom-navigation"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          borderTop: "1px solid #dee2e6",
          zIndex: 1000,
        }}
      >
        <div className="d-flex">
          <Link
            to={routes.parentDashboard}
            className="flex-fill text-center p-3 text-muted"
          >
            <i className="ti ti-home d-block"></i>
            <small>Dashboard</small>
          </Link>
          <Link
            to={routes.parentHomework}
            className="flex-fill text-center p-3 text-muted"
          >
            <i className="ti ti-book-2 d-block"></i>
            <small>Homework</small>
          </Link>
          <Link
            to={routes.parentTasks}
            className="flex-fill text-center p-3 text-muted"
          >
            <i className="ti ti-clipboard-list d-block"></i>
            <small>Tasks</small>
          </Link>
          <Link
            to={routes.parentMenu}
            className="flex-fill text-center p-3 text-primary"
          >
            <i className="ti ti-menu-2 d-block"></i>
            <small>Menu</small>
          </Link>
        </div>
      </div>
    </>
  );
};

export default ParentHelp;
